import React from 'react';
import { Eye, EyeOff, ArrowLeft, Mail, CheckCircle, Loader2, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useForgotPasswordForm from '../../hooks/useForgotPasswordForm';

/**
 * Forgot Password Variant — Dark Glass (Theme 3 "Bold")
 * Full dark background with animated purple/pink/blue gradient orbs,
 * centered glassmorphism card, 3-step reset flow with step indicator.
 */
const ForgotPasswordGlass = () => {
  const { t } = useTranslation('auth');
  const {
    step,
    email,
    code,
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    loading,
    error,
    resendTimer,
    codeInputRefs,
    setShowPassword,
    setShowConfirmPassword,
    handleEmailChange,
    handleSendCode,
    handleCodeChange,
    handleCodeKeyDown,
    handleCodePaste,
    handleVerifyCode,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleResetPassword,
    handleResendCode,
    goBackToEmail,
  } = useForgotPasswordForm();

  const steps = [1, 2, 3];

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center relative overflow-hidden">
      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 -left-20 w-[500px] h-[500px] bg-purple-600/30 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-pink-500/20 rounded-full blur-3xl animate-pulse [animation-delay:2s]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-3xl animate-pulse [animation-delay:4s]" />

      {/* Main Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Glow behind card */}
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 rounded-[20px] blur-xl opacity-60" />

        <div className="relative bg-white/[0.05] backdrop-blur-xl border border-white/10 rounded-2xl p-8 sm:p-10 shadow-2xl">
          {/* Back to login */}
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 rtl:-scale-x-100" />
            {t('forgotPassword.backToLogin')}
          </Link>

          {/* Secure Reset badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/[0.06] border border-white/10 rounded-full text-xs text-purple-300 font-medium">
              <Shield className="w-3.5 h-3.5" />
              {t('forgotPassword.secureReset', 'Secure Reset')}
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-0 mb-8">
            {steps.map((s, idx) => (
              <React.Fragment key={s}>
                {/* Step circle */}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    s <= step
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : 'bg-white/10 text-white/40'
                  }`}
                >
                  {s < step ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    s
                  )}
                </div>
                {/* Connecting line */}
                {idx < steps.length - 1 && (
                  <div
                    className={`w-12 h-0.5 transition-all ${
                      s < step
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                        : 'bg-white/10'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Error display */}
          {error && (
            <div className="p-3 mb-6 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          {/* ── Step 1: Email ── */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-6">
              {/* Icon */}
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Mail className="w-7 h-7 text-white" />
                </div>
              </div>

              {/* Heading */}
              <div className="text-center">
                <h1 className="text-2xl font-extrabold text-white mb-2">
                  {t('forgotPassword.titleAlt')}
                </h1>
                <p className="text-sm text-white/60">
                  {t('forgotPassword.descriptionAlt')}
                </p>
              </div>

              {/* Email input */}
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder={t('forgotPassword.enterEmail')}
                  className="w-full px-4 py-3.5 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:bg-white/[0.08] transition-all"
                  required
                />
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 group-hover:from-purple-400 group-hover:to-pink-400 transition-all" />
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('forgotPassword.sending')}
                    </>
                  ) : (
                    t('forgotPassword.sendCode')
                  )}
                </span>
              </button>
            </form>
          )}

          {/* ── Step 2: Verification Code ── */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              {/* Heading */}
              <div className="text-center">
                <h1 className="text-2xl font-extrabold text-white mb-2">
                  {t('forgotPassword.verifyTitle')}
                </h1>
                <p className="text-sm text-white/60">
                  {t('forgotPassword.codeSentTo')}{' '}
                  <span className="text-purple-400 font-medium">{email}</span>
                </p>
              </div>

              {/* 6-digit code inputs */}
              <div className="flex justify-center gap-2.5" onPaste={handleCodePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (codeInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className="w-11 h-13 sm:w-12 sm:h-14 text-center text-lg font-bold bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:bg-white/[0.08] transition-all"
                  />
                ))}
              </div>

              {/* Verify button */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 group-hover:from-purple-400 group-hover:to-pink-400 transition-all" />
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('forgotPassword.verifying')}
                    </>
                  ) : (
                    t('forgotPassword.verifyCode')
                  )}
                </span>
              </button>

              {/* Resend code with timer */}
              <div className="text-center space-y-2">
                <p className="text-sm text-white/60">
                  {t('forgotPassword.didntReceive')}{' '}
                  {resendTimer > 0 ? (
                    <span className="text-purple-400 font-medium">
                      {t('forgotPassword.resendIn', { seconds: resendTimer })}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={loading}
                      className="text-purple-400 hover:text-purple-300 font-medium transition-colors disabled:opacity-50"
                    >
                      {t('forgotPassword.resendCode')}
                    </button>
                  )}
                </p>
                <button
                  type="button"
                  onClick={goBackToEmail}
                  className="text-sm text-white/40 hover:text-white/60 transition-colors"
                >
                  {t('forgotPassword.useDifferentEmail')}
                </button>
              </div>
            </form>
          )}

          {/* ── Step 3: New Password ── */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {/* Icon */}
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
              </div>

              {/* Heading */}
              <div className="text-center">
                <h1 className="text-2xl font-extrabold text-white mb-2">
                  {t('forgotPassword.newPasswordTitle')}
                </h1>
                <p className="text-sm text-white/60">
                  {t('forgotPassword.newPasswordDescription')}
                </p>
              </div>

              {/* New password */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  {t('forgotPassword.newPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3.5 pe-12 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:bg-white/[0.08] transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute end-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  {t('forgotPassword.confirmNewPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3.5 pe-12 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:bg-white/[0.08] transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute end-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Reset password button */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 group-hover:from-purple-400 group-hover:to-pink-400 transition-all" />
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('forgotPassword.resetting')}
                    </>
                  ) : (
                    t('forgotPassword.resetPassword')
                  )}
                </span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordGlass;
